import { signTypedData } from '@wagmi/core';
import { Address, Hex } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import { Config } from 'wagmi';
import { SignatureType } from '../constants';
import {
  SchemaDomainTypeDto,
  SchemaSubmitDryOrderDto,
  SchemaSubmitOrderDto,
} from '../generated/apiTypes';
import { getSpecificSignatureType, SignTypedDataTypes } from '../hooks/useDataForSigning';
import { ProductType } from '../hooks/useProducts';
import { SubaccountData } from '../hooks/useWalletStorage';
import { TradeOrder } from '../types/generated';
import { ValidatedOrder } from '../types/orders';
import { Gwei } from './gwei';
import { createOrderDataDto } from './orderHelpers';
import { getNonceInNanoseconds, nowInSeconds } from './time';

/** Structure order data into message for signing. */
export const getOrderSignatureMessage = (data: SchemaSubmitDryOrderDto['data']) =>
  ({
    sender: data.sender,
    price: data.type === 'LIMIT' ? Gwei.toGwei(data.price) : 0n,
    reduceOnly: data.reduceOnly ?? false,
    quantity: Gwei.toGwei(data.quantity),
    side: data.side,
    subaccount: data.subaccount,
    productId: data.onchainId,
    engineType: data.engineType,
    nonce: BigInt(data.nonce),
    signedAt: BigInt(data.signedAt),
  }) satisfies TradeOrder;

/** Sign order data using the signing method configured for the subaccount. */
export const signOrderData = async ({
  data,
  subaccountData,
  domain,
  signedTypedDataTypes,
  config,
}: {
  data: SchemaSubmitDryOrderDto['data'];
  subaccountData: SubaccountData | undefined;
  domain: SchemaDomainTypeDto;
  signedTypedDataTypes: SignTypedDataTypes;
  config: Config;
}): Promise<Hex> => {
  const message = getOrderSignatureMessage(data);

  const signatureParams = {
    domain: domain,
    types: getSpecificSignatureType(signedTypedDataTypes, SignatureType.TRADE_ORDER),
    primaryType: SignatureType.TRADE_ORDER,
    message,
  };

  return subaccountData?.signingMethod === 'one-click'
    ? await privateKeyToAccount(subaccountData.signerKey).signTypedData(signatureParams)
    : await signTypedData(config, signatureParams);
};

/** Create and sign the data required to submit an order. */
export const createOrderSubmitBody = async ({
  partialOrderData,
  selectedSubaccount,
  subaccountData,
  product,
  connectedAddress,
  domain,
  signedTypedDataTypes,
  config,
}: {
  partialOrderData: ValidatedOrder;
  selectedSubaccount: { name: Hex };
  subaccountData: SubaccountData | undefined;
  product: ProductType;
  connectedAddress: Address;
  domain: SchemaDomainTypeDto;
  signedTypedDataTypes: SignTypedDataTypes;
  config: Config;
}): Promise<SchemaSubmitOrderDto> => {
  const nonce = getNonceInNanoseconds();
  const signedAt = nowInSeconds();

  const sender: Address =
    subaccountData?.signingMethod === 'one-click'
      ? privateKeyToAccount(subaccountData.signerKey).address
      : connectedAddress;

  const data = createOrderDataDto({
    subaccountName: selectedSubaccount.name,
    sender,
    product,
    nonce: nonce.toString(),
    signedAt,
    ...partialOrderData,
  });

  const signature = await signOrderData({
    data,
    subaccountData,
    domain,
    signedTypedDataTypes,
    config,
  });

  return {
    signature,
    data,
  };
};
